// ====
import { parse } from "date-fns";

//@ts-ignore
import zeroPad from "zeropad";

/**
 * this function is used to convert a unix timestamp to to a string
 * that represents relative time from the current time, like - 2 hrs agao
 * of if the unix timestamp represents a time that is more than 1 day ago
 * then it will be converted to a sring that will tell the date of the unix timestamp.
 * */
export function formatTimestampToAgo(timestamp: number | string): string {
  if (typeof timestamp == "string") {
    timestamp = parseInt(timestamp);
  }

  if (isNaN(timestamp)) return "error ";

  // Convert to milliseconds if the timestamp is in seconds (i.e., it has 10 digits)
  if (timestamp.toString().length === 10) {
    timestamp = timestamp * 1000;
  }

  const now = Date.now();
  const diffInMs = now - timestamp;

  const seconds = Math.max(Math.floor(diffInMs / 1000), 0);
  const minutes = Math.max(Math.floor(seconds / 60), 0);
  const hours = Math.max(Math.floor(minutes / 60), 0);

  if (seconds < 60) {
    return seconds === 1 ? "1 sec ago" : `${seconds} secs ago`;
  } else if (minutes < 60) {
    return minutes === 1 ? "1 min ago" : `${minutes} mins ago`;
  } else if (hours < 24) {
    return hours === 1 ? "1 hr ago" : `${hours} hrs ago`;
  } else {
    const date = new Date(timestamp);
    const options: Intl.DateTimeFormatOptions = {
      day: "numeric",
      month: "short",
      year: "numeric",
    };
    return `${date.toLocaleDateString("en-US", options)}`;
  }
}

export const rangeSince = (
  minutes: number,
  field: "ct" | "mt" = "ct"
): { [k: string]: { gte: number } } | undefined =>
  minutes > 0 ? { [field]: { gte: Date.now() - minutes * 60000 } } : undefined;

export const msToHrTime = (timeInMs: number): string => {
  const hours = Math.floor(timeInMs / (1000 * 60 * 60));
  const minutes = Math.floor((timeInMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((timeInMs % (1000 * 60)) / 1000);

  const formattedHours = hours.toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");
  const formattedSeconds = seconds.toString().padStart(2, "0");

  return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
};

export function secondsToMs(d: number): string {
  d = Number(d);
  const m = Math.floor((d % 3600) / 60);
  const s = Math.floor((d % 3600) % 60);

  const mDisplay = m > 0 ? zeroPad(m) + ":" : "00:";
  const sDisplay = s > 0 ? zeroPad(s) + "" : "00";
  return mDisplay + sDisplay;
}

export function msToHms(d: number): string {
  return secondsToHms(d / 1000);
}

export function secondsToHms(d: number | undefined): string {
  d = Math.ceil(Number(d));
  const h = Math.floor(d / 3600);
  const m = Math.floor((d % 3600) / 60);
  const s = (d % 3600) % 60;

  const hDisplay = h > 0 ? (h < 10 ? "0" + h + ":" : h + ":") : "";
  const mDisplay = m > 0 || h > 0 ? zeroPadNew(m) + ":" : "00:";
  const sDisplay = s > 0 || m > 0 || h > 0 ? zeroPadNew(s) : "00";

  return hDisplay + mDisplay + sDisplay;
}

function zeroPadNew(num: number): string {
  return num < 10 ? "0" + num : num.toString();
}

export const getFormattedDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const getFormattedDateTime = (date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

export function stringToDate(
  dateString: string | number,
  formatString = "yyyy-MM-dd"
): Date | null {
  try {
    if (typeof dateString === "number") {
      return new Date(dateString);
    }
    const parsedDate = parse(dateString, formatString, new Date());
    return isNaN(parsedDate.getTime()) ? null : parsedDate;
  } catch {
    console.error("Invalid date format or date string");
    return null;
  }
}

export function isDateString(date: string): boolean {
  const regex = /^\d{4}-\d{2}-\d{2}$/; // YYYY-MM-DD format
  return regex.test(date);
}

export function stringToDateTime(inputDate: string | Date): string {
  const date = new Date(inputDate); // Convert to Date object if it's a string
  if (isNaN(date.getTime())) {
    throw new Error("Invalid date provided.");
  }

  // Extract date parts
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-based
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}`;
}
